import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { HStack, VStack } from '@terra/ui-styled-system';
import {
  Alert,
  Button,
  createSelectListCollection,
  IconButton,
  MetaDefault,
  Select,
  SettingsBooleanControl,
  SettingsRow,
  toasterApi,
  SettingsEmptyState,
} from '@terra/ui-design-system';
import {
  Add01Icon,
  Delete01Icon,
  InformationCircleIcon,
} from '@terra/ui-icons';
import { useSettingsHelpers } from '../../hooks/useSettingsHelpers';
import { useSelectedTarget } from '../../store/projectStore';
import { useApplicationCredentials } from '../../hooks/useApplicationCredentials';
import type { ClientTlsCertificateEntry } from '../../store/proxy-store';
import { ProxySettingsGroup } from './proxy-settings/ProxySettingsGroup';
import { ClientTlsManualEntryDialog } from './client-tls/ClientTlsManualEntryDialog';

export function ClientTlsCertificatesTab() {
  const t = useTranslations('settings.clientTls');
  const selectedTarget = useSelectedTarget();
  const { settings, updateSettings } = useSettingsHelpers();
  const [selectedCredentialId, setSelectedCredentialId] = useState<string[]>(
    [],
  );
  const [isManualDialogOpen, setIsManualDialogOpen] = useState(false);
  const { credentials } = useApplicationCredentials({
    applicationId: selectedTarget?.id,
    enabled: Boolean(selectedTarget?.id),
  });
  const entries = settings.clientTlsCertificates;
  const mtlsCredentials = useMemo(
    () =>
      credentials.filter(
        (credential) =>
          Boolean(credential.mtlsOrigin || selectedTarget?.url) &&
          Boolean(
            credential.mtlsPfx || (credential.mtlsCert && credential.mtlsKey),
          ),
      ),
    [credentials, selectedTarget?.url],
  );
  const credentialsCollection = useMemo(
    () =>
      createSelectListCollection({
        items: mtlsCredentials.map((credential) => ({
          label: `${credential.role || credential.username} (${
            credential.mtlsOrigin ||
            selectedTarget?.url ||
            t('manualEntries.noOriginFallback')
          })`,
          value: credential.id,
        })),
      }),
    [mtlsCredentials, selectedTarget?.url, t],
  );

  const setEntries = (clientTlsCertificates: ClientTlsCertificateEntry[]) =>
    updateSettings({ clientTlsCertificates });

  const addCredentialEntry = () => {
    const credential = mtlsCredentials.find(
      (candidate) => candidate.id === selectedCredentialId[0],
    );

    if (!credential) {
      return;
    }

    const origin = credential.mtlsOrigin?.trim() || selectedTarget?.url || '';

    if (!origin) {
      toasterApi.error({
        title: t('addFromCredentials.missingOrigin'),
        description: t('addFromCredentials.missingOriginDescription'),
      });

      return;
    }

    const entryId = `credential:${credential.id}`;

    if (entries.some((entry) => entry.id === entryId)) {
      toasterApi.info({
        title: t('addFromCredentials.alreadyAdded'),
        description: t('addFromCredentials.alreadyAddedDescription'),
      });

      return;
    }

    setEntries([
      ...entries,
      {
        id: entryId,
        name: t('addFromCredentials.credentialLabel', {
          name: credential.role || credential.username,
        }),
        origin,
        cert: credential.mtlsCert || undefined,
        key: credential.mtlsKey || undefined,
        pfx: credential.mtlsPfx || undefined,
        passphrase: credential.mtlsPassphrase || undefined,
        enabled: true,
        source: 'credential',
        credentialId: credential.id,
      },
    ]);
    setSelectedCredentialId([]);
  };

  const renderCredentialPicker = () => {
    if (!selectedTarget) {
      return (
        <MetaDefault color="fg.muted">
          {t('addFromCredentials.selectAnApplication')}
        </MetaDefault>
      );
    }

    if (mtlsCredentials.length === 0) {
      return (
        <MetaDefault color="fg.muted">
          {t('addFromCredentials.noCredentials')}
        </MetaDefault>
      );
    }

    return (
      <SettingsRow
        label={selectedTarget.name}
        control={
          <HStack gap="2">
            <Select.Root
              collection={credentialsCollection}
              value={selectedCredentialId}
              onValueChange={(details) =>
                setSelectedCredentialId(details.value)
              }
              size="sm"
              w="280px"
            >
              <Select.Control w="full">
                <Select.Trigger>
                  <Select.ValueText
                    placeholder={t('addFromCredentials.selectPlaceholder')}
                  />
                </Select.Trigger>
              </Select.Control>
              <Select.Positioner>
                <Select.Content>
                  <Select.ItemGroup>
                    {credentialsCollection.items.map((item) => (
                      <Select.Item key={item.value} item={item}>
                        {item.label}
                        <Select.ItemIndicator />
                      </Select.Item>
                    ))}
                  </Select.ItemGroup>
                </Select.Content>
              </Select.Positioner>
            </Select.Root>
            <Button
              size="xs"
              variant="outline"
              onClick={addCredentialEntry}
              disabled={selectedCredentialId.length === 0}
            >
              <Add01Icon size={14} />
              {t('addFromCredentials.addButton')}
            </Button>
          </HStack>
        }
      />
    );
  };

  return (
    <>
      <VStack gap={6} alignItems="stretch" w="full">
        <Alert.Root>
          <Alert.Icon asChild>
            <InformationCircleIcon />
          </Alert.Icon>
          <Alert.Content>
            <Alert.Title>{t('howThisWorks')}</Alert.Title>
            <Alert.Description>
              {t('howThisWorksDescription')}
            </Alert.Description>
          </Alert.Content>
        </Alert.Root>

        <ProxySettingsGroup
          title={t('addFromCredentials.title')}
          description={
            selectedTarget?.name
              ? t('addFromCredentials.subtitleNamed', {
                  applicationName: selectedTarget.name,
                })
              : t('addFromCredentials.subtitleGeneric')
          }
        >
          {renderCredentialPicker()}
        </ProxySettingsGroup>

        <ProxySettingsGroup
          title={t('configured.title')}
          description={
            entries.length === 1
              ? t('configured.countOne', { count: entries.length })
              : t('configured.countOther', { count: entries.length })
          }
          tooltip={t('manualEntries.subtitle')}
          action={
            <Button
              size="xs"
              variant="outline"
              onClick={() => setIsManualDialogOpen(true)}
            >
              <Add01Icon size={14} />
              {t('manualEntries.addButton')}
            </Button>
          }
        >
          {entries.length === 0 ? (
            <SettingsEmptyState>{t('configured.empty')}</SettingsEmptyState>
          ) : (
            entries.map((entry) => (
              <SettingsRow
                key={entry.id}
                label={entry.name}
                description={`${
                  entry.source === 'credential'
                    ? t('sourceLabel.credential')
                    : t('sourceLabel.manual')
                } · ${entry.origin}`}
                control={
                  <HStack gap="2">
                    <SettingsBooleanControl
                      checked={entry.enabled}
                      onCheckedChange={(details) =>
                        setEntries(
                          entries.map((current) =>
                            current.id === entry.id
                              ? { ...current, enabled: details.checked }
                              : current,
                          ),
                        )
                      }
                    />
                    <IconButton
                      size="xs"
                      variant="ghost"
                      aria-label={t('configured.removeAriaLabel')}
                      onClick={() =>
                        setEntries(
                          entries.filter((current) => current.id !== entry.id),
                        )
                      }
                    >
                      <Delete01Icon size={14} />
                    </IconButton>
                  </HStack>
                }
              />
            ))
          )}
        </ProxySettingsGroup>
      </VStack>

      <ClientTlsManualEntryDialog
        open={isManualDialogOpen}
        onClose={() => setIsManualDialogOpen(false)}
        onAdd={(entry) => setEntries([...entries, entry])}
      />
    </>
  );
}
